import { Disposable, IDisposable, toDisposable } from "../../../base/common/lifecycle.js";
import { WebHIDApi, WebHIDConnectionEvent, WebHIDDevice, WebHIDInputReportEvent, WebHIDRequestDeviceOptions, webHidDeviceKey } from "../common/webHid.js";

export interface BrowserWebHIDDeviceCollectionErrorContext {
    phase: "restore" | "request" | "connect" | "attach" | "input";
    device?: WebHIDDevice;
}

export interface BrowserWebHIDDeviceCollectionOptions<TState> {
    hid?: WebHIDApi;
    requestOptions: WebHIDRequestDeviceOptions;
    supportsDevice(device: WebHIDDevice): boolean;
    createState(device: WebHIDDevice): TState;
    updateState(state: TState, event: WebHIDInputReportEvent, device: WebHIDDevice): void;
    onDeviceConnected?(device: WebHIDDevice, key: string): void;
    onDeviceDisconnected?(device: WebHIDDevice, key: string): void;
    onError?(error: unknown, context: BrowserWebHIDDeviceCollectionErrorContext): void;
}

export class BrowserWebHIDDeviceCollection<TState> extends Disposable {

    private readonly devices = new Map<string, WebHIDDevice>();
    private readonly states = new Map<string, TState>();
    private readonly listeners = new Map<string, IDisposable>();

    constructor(private readonly options: BrowserWebHIDDeviceCollectionOptions<TState>) {
        super();

        if (!this.options.hid) {
            return;
        }

        this.options.hid.addEventListener("connect", this.onHidConnect);
        this.options.hid.addEventListener("disconnect", this.onHidDisconnect);
        this._register(toDisposable(() => {
            this.options.hid?.removeEventListener("connect", this.onHidConnect);
            this.options.hid?.removeEventListener("disconnect", this.onHidDisconnect);
        }));
    }

    get available(): boolean {
        return !!this.options.hid;
    }

    get hasDevices(): boolean {
        return this.devices.size > 0;
    }

    getStates(): IterableIterator<TState> {
        return this.states.values();
    }

    getDevices(): IterableIterator<WebHIDDevice> {
        return this.devices.values();
    }

    async restoreGrantedDevices(): Promise<void> {
        if (!this.options.hid) return;
        try {
            const devices = await this.options.hid.getDevices();
            for (const device of devices) {
                if (!this.options.supportsDevice(device)) continue;
                await this.attachDevice(device);
            }
        }
        catch (error) {
            this.options.onError?.(error, { phase: "restore" });
        }
    }

    async requestAccess(): Promise<boolean> {
        if (!this.options.hid) return false;
        try {
            const devices = await this.options.hid.requestDevice(this.options.requestOptions);
            let connected = false;
            for (const device of devices) {
                connected = (await this.attachDevice(device)) || connected;
            }
            return connected;
        }
        catch (error) {
            this.options.onError?.(error, { phase: "request" });
            return false;
        }
    }

    override dispose(): void {
        for (const disposable of this.listeners.values()) {
            disposable.dispose();
        }
        this.listeners.clear();
        this.devices.clear();
        this.states.clear();
        super.dispose();
    }

    private attachDevice = async (device: WebHIDDevice): Promise<boolean> => {
        if (!this.options.supportsDevice(device)) {
            return false;
        }

        try {
            if (!device.opened) {
                await device.open();
            }

            const key = webHidDeviceKey(device);
            if (this.listeners.has(key)) {
                return true;
            }

            const listener: EventListener = event => {
                const report = event as WebHIDInputReportEvent;
                try {
                    const state = this.states.get(key) ?? this.options.createState(device);
                    this.options.updateState(state, report, device);
                    this.states.set(key, state);
                }
                catch (error) {
                    this.options.onError?.(error, { phase: "input", device });
                }
            };

            device.addEventListener("inputreport", listener);
            this.listeners.set(key, toDisposable(() => device.removeEventListener("inputreport", listener)));
            this.devices.set(key, device);
            if (!this.states.has(key)) {
                this.states.set(key, this.options.createState(device));
            }
            this.options.onDeviceConnected?.(device, key);
            return true;
        }
        catch (error) {
            this.options.onError?.(error, { phase: "attach", device });
            return false;
        }
    };

    private detachDevice(device: WebHIDDevice): void {
        const key = webHidDeviceKey(device);
        this.listeners.get(key)?.dispose();
        this.listeners.delete(key);
        this.devices.delete(key);
        this.states.delete(key);
        this.options.onDeviceDisconnected?.(device, key);
    }

    private onHidConnect = (event: Event) => {
        const hidEvent = event as WebHIDConnectionEvent;
        void this.attachDevice(hidEvent.device).catch(error => {
            this.options.onError?.(error, { phase: "connect", device: hidEvent.device });
        });
    };

    private onHidDisconnect = (event: Event) => {
        const hidEvent = event as WebHIDConnectionEvent;
        this.detachDevice(hidEvent.device);
    };
}
